// 담당자 개인 권한 관리 탭 — 개인 권한 부여/회수 + 최종 권한(Effective) 미리보기

import { useMemo } from "react";
import { toast } from "sonner";
import {
  useAccountEffectivePermissions,
  useAccountPermissions,
  useGrantAccountPermission,
  usePermissionsByCategory,
  useRevokeAccountPermission,
} from "../hooks/useAccountPermissions";
import { groupPermissions } from "../utils/groupPermissions";
import { getPermissionGroupLabel } from "../constants/permission.constants";
import { ServiceCategory } from "../types/permission.types";
import { PermissionCheckboxGroup } from "./PermissionCheckboxGroup";

interface AccountPermissionTabProps {
  accountGuid: string;
}

export function AccountPermissionTab({ accountGuid }: AccountPermissionTabProps) {
  // 권한 데이터 (배차 카테고리 한정)
  const { data: allPermissions = [], isLoading: isLoadingPerms } =
    usePermissionsByCategory(ServiceCategory.DISPATCH);
  const { data: directPermissions = [], isLoading: isLoadingDirect } =
    useAccountPermissions(accountGuid);
  const { data: effectiveCodes = [], isLoading: isLoadingEffective } =
    useAccountEffectivePermissions(accountGuid);

  const directPermissionGuids = useMemo(
    () => new Set(directPermissions.map((p) => p.permissionGuid)),
    [directPermissions],
  );

  // 역할에서 상속된 권한 = effective 에는 있으나 직접 부여가 아닌 코드
  const inheritedPermissionGuids = useMemo(() => {
    const direct = new Set(directPermissions.map((p) => p.permissionCode));
    const inheritedCodes = new Set(
      effectiveCodes.filter((c) => !direct.has(c)),
    );
    const codeToGuid = new Map(allPermissions.map((p) => [p.code, p.guid]));
    const set = new Set<string>();
    for (const code of inheritedCodes) {
      const guid = codeToGuid.get(code);
      if (guid) set.add(guid);
    }
    return set;
  }, [effectiveCodes, directPermissions, allPermissions]);

  const grantPerm = useGrantAccountPermission();
  const revokePerm = useRevokeAccountPermission();

  const handleTogglePermission = (permissionGuid: string, checked: boolean) => {
    if (checked) {
      grantPerm.mutate(
        { accountGuid, permissionGuid },
        {
          onSuccess: () => toast.success("권한이 부여되었습니다."),
          onError: (err) => toast.error(`부여 실패: ${err.message}`),
        },
      );
    } else {
      revokePerm.mutate(
        { accountGuid, permissionGuid },
        {
          onSuccess: () => toast.success("권한이 회수되었습니다."),
          onError: (err) => toast.error(`회수 실패: ${err.message}`),
        },
      );
    }
  };

  const groupedPermissions = useMemo(
    () => groupPermissions(allPermissions),
    [allPermissions],
  );

  // Effective 코드 → permission master를 통해 그룹별 표시
  const effectiveByGroup = useMemo(() => {
    const codeToInfo = new Map(
      allPermissions.map((p) => [p.code, { group: p.permissionGroup }]),
    );
    const buckets = new Map<string, string[]>();
    for (const code of effectiveCodes) {
      const group = codeToInfo.get(code)?.group ?? "etc";
      if (!buckets.has(group)) buckets.set(group, []);
      buckets.get(group)!.push(code);
    }
    return Array.from(buckets.entries()).map(([groupCode, codes]) => ({
      groupCode,
      groupLabel: getPermissionGroupLabel(groupCode),
      codes: codes.sort((a, b) => a.localeCompare(b)),
    }));
  }, [effectiveCodes, allPermissions]);

  const isToggling = grantPerm.isPending || revokePerm.isPending;

  return (
    <div className="space-y-4">
      {/* 섹션 A — 개인 권한 */}
      <div className="overflow-hidden rounded-lg border border-emerald-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 px-3 py-2">
          <h3 className="text-sm font-semibold text-gray-900">개인 권한</h3>
          <span className="text-[11px] text-gray-500">
            직접 부여 {directPermissionGuids.size} · 상속{" "}
            {inheritedPermissionGuids.size}
          </span>
        </div>
        <div className="p-3">
          {isLoadingPerms || isLoadingDirect ? (
            <div className="py-6 text-center text-xs text-gray-400">
              로딩 중...
            </div>
          ) : groupedPermissions.length === 0 ? (
            <div className="py-6 text-center text-xs text-gray-400">
              사용 가능한 권한이 없습니다.
            </div>
          ) : (
            <PermissionCheckboxGroup
              groups={groupedPermissions}
              selectedGuids={directPermissionGuids}
              onToggle={handleTogglePermission}
              inheritedGuids={inheritedPermissionGuids}
              disabled={isToggling}
            />
          )}
          <p className="mt-3 text-[10px] text-gray-500">
            "역할 상속"으로 표시된 권한은 부여된 역할에서 자동 상속되며, 개별
            토글로 회수할 수 없습니다.
          </p>
        </div>
      </div>

      {/* 섹션 B — 최종 권한(Effective) 미리보기 */}
      <div className="overflow-hidden rounded-lg border border-purple-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-purple-200 bg-gradient-to-r from-purple-50 to-fuchsia-50 px-3 py-2">
          <h3 className="text-sm font-semibold text-gray-900">
            최종 권한 (Effective)
          </h3>
          <span className="text-[11px] text-gray-500">
            총 {effectiveCodes.length}개
          </span>
        </div>
        <div className="space-y-2 p-3">
          {isLoadingEffective ? (
            <div className="py-6 text-center text-xs text-gray-400">
              로딩 중...
            </div>
          ) : effectiveByGroup.length === 0 ? (
            <div className="py-6 text-center text-xs text-gray-400">
              부여된 권한이 없습니다.
            </div>
          ) : (
            effectiveByGroup.map((g) => (
              <div key={g.groupCode}>
                <div className="mb-1 text-[10px] font-semibold text-gray-600">
                  {g.groupLabel}
                </div>
                <div className="flex flex-wrap gap-1">
                  {g.codes.map((c) => (
                    <span
                      key={c}
                      className="rounded border border-purple-200 bg-purple-50 px-1.5 py-0.5 font-mono text-[10px] text-purple-700"
                    >
                      {c}
                    </span>
                  ))}
                </div>
              </div>
            ))
          )}
          <div className="mt-2 rounded border border-blue-100 bg-blue-50 px-3 py-2 text-[11px] text-blue-700">
            변경 사항은 담당자가 다시 로그인한 후 즉시 반영됩니다 (JWT 만료
            전까지는 옛 권한 유지).
          </div>
        </div>
      </div>
    </div>
  );
}
